/**
 * Autosaving edit buffer for the per-platform variants.
 *
 * Typing stays local and instant (the previews render from this buffer), and a
 * debounced `PATCH /api/platform-posts/:id` persists it. The buffer entry is
 * dropped once the server has the exact value that was sent, so server-side
 * normalisation (hashtags, trimming) flows back into the editor without ever
 * clobbering keystrokes that landed while the request was in flight.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PlatformId, PostDetail } from '../../../shared/types';
import { api } from '../../lib/api';
import type { DraftValue, SaveState } from './types';
import { isDraftEqual } from './types';

const AUTOSAVE_DELAY_MS = 900;

export interface ComposerDrafts {
  /** Current value for a variant: local edits merged over the server copy. */
  value: (platformPostId: string) => DraftValue;
  /** Replace a variant's value, scheduling (or forcing) a save. */
  apply: (
    platformPostId: string,
    next: DraftValue | ((current: DraftValue) => DraftValue),
    options?: { immediate?: boolean },
  ) => void;
  state: (platformPostId: string) => SaveState;
  /** Any variant with unsaved keystrokes. */
  pending: boolean;
  /** Persist everything outstanding — call before publishing or scheduling. */
  flush: () => Promise<void>;
  /** Forget local edits for a variant (after a regenerate, say). */
  reset: (platformPostId: string) => void;
}

const EMPTY: DraftValue = { content: '', meta: {}, media: [] };

export interface UseComposerDraftsOptions {
  post: PostDetail | null;
  /** Called with the post detail every save returns, to refresh the cache. */
  onSynced: (post: PostDetail) => void;
  onError: (platform: PlatformId, error: unknown) => void;
}

export function useComposerDrafts({
  post,
  onSynced,
  onError,
}: UseComposerDraftsOptions): ComposerDrafts {
  const [edits, setEdits] = useState<Record<string, DraftValue>>({});
  const [states, setStates] = useState<Record<string, SaveState>>({});

  const editsRef = useRef(edits);
  const timers = useRef(new Map<string, number>());
  const inflight = useRef(new Map<string, Promise<void>>());
  const postRef = useRef(post);
  const handlers = useRef({ onSynced, onError });

  useEffect(() => {
    handlers.current = { onSynced, onError };
  });

  useEffect(() => {
    editsRef.current = edits;
  }, [edits]);

  const serverValues = useMemo(() => {
    const map = new Map<string, DraftValue>();
    for (const variant of post?.platformPosts ?? []) {
      map.set(variant.id, {
        content: variant.content,
        meta: variant.meta,
        media: variant.media,
      });
    }
    return map;
  }, [post]);

  const platformOf = useMemo(() => {
    const map = new Map<string, PlatformId>();
    for (const variant of post?.platformPosts ?? []) map.set(variant.id, variant.platform);
    return map;
  }, [post]);

  useEffect(() => {
    postRef.current = post;
    if (!post) return;
    // A variant can disappear (platform deselected) — drop its buffer.
    const live = new Set(post.platformPosts.map((variant) => variant.id));
    setEdits((current) => {
      const stale = Object.keys(current).filter((id) => !live.has(id));
      if (!stale.length) return current;
      const next = { ...current };
      for (const id of stale) delete next[id];
      return next;
    });
  }, [post]);

  const serverValueOf = useCallback((platformPostId: string): DraftValue => {
    const variant = postRef.current?.platformPosts.find((item) => item.id === platformPostId);
    if (!variant) return EMPTY;
    return { content: variant.content, meta: variant.meta, media: variant.media };
  }, []);

  const save = useCallback(
    async (platformPostId: string, override?: DraftValue): Promise<void> => {
      const value = override ?? editsRef.current[platformPostId];
      if (!value) return;

      const existing = inflight.current.get(platformPostId);
      if (existing) await existing.catch(() => undefined);

      const run = (async () => {
        setStates((current) => ({ ...current, [platformPostId]: 'saving' }));
        try {
          const response = await api.platformPosts.update(platformPostId, {
            content: value.content,
            meta: value.meta,
            media: value.media,
          });
          handlers.current.onSynced(response.post);

          const latest = editsRef.current[platformPostId];
          if (!latest || isDraftEqual(latest, value)) {
            setEdits((current) => {
              if (!(platformPostId in current)) return current;
              const next = { ...current };
              delete next[platformPostId];
              return next;
            });
            setStates((current) => ({ ...current, [platformPostId]: 'saved' }));
          } else {
            setStates((current) => ({ ...current, [platformPostId]: 'dirty' }));
          }
        } catch (error) {
          setStates((current) => ({ ...current, [platformPostId]: 'error' }));
          handlers.current.onError(platformOf.get(platformPostId) ?? 'x', error);
        }
      })();

      inflight.current.set(platformPostId, run);
      await run;
      if (inflight.current.get(platformPostId) === run) inflight.current.delete(platformPostId);
    },
    [platformOf],
  );

  const apply = useCallback<ComposerDrafts['apply']>(
    (platformPostId, next, options) => {
      const base = editsRef.current[platformPostId] ?? serverValueOf(platformPostId);
      const value = typeof next === 'function' ? next(base) : next;

      editsRef.current = { ...editsRef.current, [platformPostId]: value };
      setEdits((current) => ({ ...current, [platformPostId]: value }));
      setStates((current) => ({ ...current, [platformPostId]: 'dirty' }));

      const timer = timers.current.get(platformPostId);
      if (timer !== undefined) window.clearTimeout(timer);

      if (options?.immediate) {
        timers.current.delete(platformPostId);
        void save(platformPostId, value);
        return;
      }

      timers.current.set(
        platformPostId,
        window.setTimeout(() => {
          timers.current.delete(platformPostId);
          void save(platformPostId, value);
        }, AUTOSAVE_DELAY_MS),
      );
    },
    [save, serverValueOf],
  );

  const flush = useCallback(async () => {
    const ids = Object.keys(editsRef.current);
    for (const id of ids) {
      const timer = timers.current.get(id);
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timers.current.delete(id);
      }
    }
    await Promise.all(ids.map((id) => save(id)));
    await Promise.all([...inflight.current.values()].map((promise) => promise.catch(() => undefined)));
  }, [save]);

  const reset = useCallback((platformPostId: string) => {
    const timer = timers.current.get(platformPostId);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(platformPostId);
    }
    const withoutEntry = { ...editsRef.current };
    delete withoutEntry[platformPostId];
    editsRef.current = withoutEntry;
    setEdits((current) => {
      if (!(platformPostId in current)) return current;
      const next = { ...current };
      delete next[platformPostId];
      return next;
    });
    setStates((current) => ({ ...current, [platformPostId]: 'clean' }));
  }, []);

  // Navigating away mid-keystroke should still land the edit.
  useEffect(() => {
    const pendingTimers = timers.current;
    return () => {
      for (const [id, timer] of pendingTimers) {
        window.clearTimeout(timer);
        const value = editsRef.current[id];
        if (value) {
          void api.platformPosts
            .update(id, { content: value.content, meta: value.meta, media: value.media })
            .catch(() => undefined);
        }
      }
      pendingTimers.clear();
    };
  }, []);

  const value = useCallback(
    (platformPostId: string): DraftValue =>
      edits[platformPostId] ?? serverValues.get(platformPostId) ?? EMPTY,
    [edits, serverValues],
  );

  const state = useCallback(
    (platformPostId: string): SaveState => states[platformPostId] ?? 'clean',
    [states],
  );

  return {
    value,
    apply,
    state,
    pending: Object.keys(edits).length > 0,
    flush,
    reset,
  };
}
